/**
 * FloorplanTabs — 평면도 (v2 목업 상세 04: 타입 칩 탭 + 이미지, PC 좌 이미지 / 우 스펙).
 *
 * 순수 프레젠테이션 + 선택 상태만 로컬. 스펙은 같은 타입명의 type_prices 행(전용·공급·분양가·세대).
 */

import React, { useState } from 'react';
import type { FloorPlanImage } from '../../../domain/property/sectionAdapters';
import type { TypePriceRow } from '../../../domain/property/detailV2';
import { SectionCard } from './SectionCard';
import { PlanIcon } from './SectionIcons';
import { Lightbox } from '../Lightbox';

export interface FloorplanTabsProps {
  title?: string;
  images: FloorPlanImage[];
  priceRows: TypePriceRow[];
  priceLabel: string;
  /** 타입명 → 평수 라벨("35평") — 2026-09-26. 없으면 priceRows 의 pyeong 사용 */
  pyeongOf?: (typeName: string) => string | null;
}

const norm = (s: string) => s.replace(/\s+/g, '').toUpperCase();

function matchRow(typeName: string, rows: TypePriceRow[]): TypePriceRow | null {
  const t = norm(typeName);
  if (!t) return null;
  return (
    rows.find((r) => norm(r.type) === t) ||
    rows.find((r) => norm(r.type).startsWith(t) || t.startsWith(norm(r.type))) ||
    null
  );
}

export function FloorplanTabs({ title = '평면도', images, priceRows, priceLabel, pyeongOf }: FloorplanTabsProps) {
  const [idx, setIdx] = useState(0);
  const [viewer, setViewer] = useState(false);
  if (images.length === 0) return null;
  const sel = images[Math.min(idx, images.length - 1)];
  const row = matchRow(sel.typeName, priceRows);
  const pyOf = (name: string) => (pyeongOf ? pyeongOf(name) : matchRow(name, priceRows)?.pyeong ?? null);
  const selPy = pyOf(sel.typeName);
  const specs: { k: string; v: string }[] = row
    ? [
        ...(row.exclusive ? [{ k: '전용', v: row.exclusive }] : []),
        ...(row.supply ? [{ k: '공급', v: selPy && !selPy.startsWith('전용') ? `${row.supply} (${selPy})` : row.supply }] : []),
        { k: priceLabel, v: row.price },
        ...(row.units != null ? [{ k: '세대', v: `${row.units.toLocaleString()}세대` }] : []),
      ]
    : [];

  return (
    <SectionCard title={title} icon={<PlanIcon />}>
      {images.length > 1 && (
        <div className="-mx-4 mb-2.5 flex gap-2 overflow-x-auto px-4" style={{ scrollbarWidth: 'none' }} role="tablist">
          {images.map((img, i) => (
            <button
              key={img.id}
              type="button"
              role="tab"
              aria-selected={i === idx}
              onClick={() => setIdx(i)}
              className={`shrink-0 rounded-full border px-3.5 py-[7px] text-[12px] font-semibold ${
                i === idx ? 'border-ink bg-ink text-white' : 'border-ca-line bg-frame text-body'
              }`}
            >
              {img.typeName || `타입 ${i + 1}`}
              {pyOf(img.typeName) && <span className={`ml-1 font-bold ${i === idx ? 'text-white/80' : 'text-accent-dark'}`}>{pyOf(img.typeName)}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        {/* 누르면 크게 보기 (2026-09-26 대표: 세로 평면도가 너무 작다) — 확대·넘기기는 Lightbox */}
        <button
          type="button"
          onClick={() => setViewer(true)}
          className="group relative w-full cursor-zoom-in md:w-[62%]"
          aria-label={`${sel.typeName} 평면도 크게 보기`}
        >
          <img
            src={sel.imageUrl}
            alt={`${sel.typeName} 평면도`}
            loading="lazy"
            className="aspect-[16/10] w-full rounded-ca-xl bg-paper object-contain"
          />
          <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold text-white shadow-md" style={{ background: 'rgba(15,23,42,0.82)' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M11 8v6M8 11h6" />
            </svg>
            크게 보기
          </span>
        </button>
        {specs.length > 0 && (
          <dl className="grid grid-cols-2 gap-2 md:flex-1 md:grid-cols-1">
            {specs.map((s) => (
              <div key={s.k} className="rounded-ca-xl bg-ca-surface px-3 py-2">
                <dt className="text-[11px] font-semibold text-muted">{s.k}</dt>
                <dd className={`num text-[14px] font-bold ${s.k === priceLabel ? 'text-accent' : 'text-ink'}`}>{s.v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {viewer && (
        <Lightbox
          images={images.map((im) => ({ src: im.imageUrl, alt: `${im.typeName} 평면도`, caption: im.typeName || undefined }))}
          startIndex={Math.min(idx, images.length - 1)}
          onClose={() => setViewer(false)}
        />
      )}
    </SectionCard>
  );
}

export default FloorplanTabs;
